import { useState, useMemo, useCallback, useEffect } from "react";
import { useBreadcrumbs } from "@app/components/Breadcrumbs";
import {
  ActionButton,
  Switch,
  ProgressBar,
  Text,
  TextArea,
  SegmentedControl,
  SegmentedControlItem,
  Picker,
  PickerItem,
} from "@react-spectrum/s2";

import SendIcon from "@react-spectrum/s2/icons/Send";
import CodeIcon from "@react-spectrum/s2/icons/Code";
import TextAlignJustifyLastLeftIcon from "@react-spectrum/s2/icons/TextAlignJustifyLastLeft";

import { useZoltarMutation } from "@app/hooks/zoltar";

import {
  isZoltarGenerateOk,
  parseZoltarGenerateJson,
  zoltarLangchainMessagesToPromptTemplate,
} from "@app/services/zoltar";

import { SyntaxHighlighter } from "@app/components/SyntaxHighlighter";

import { style } from "@react-spectrum/s2/style" with { type: "macro" };

export const AVAILABLE_MODELS = [
  { id: "gpt-5.6-luna", name: "GPT 5.6 Luna", description: "Low cost" },
  { id: "gpt-5.6-terra", name: "GPT 5.6 Terra", description: "Medium cost" },
  { id: "gpt-5.6-sol", name: "GPT 5.6 Sol", description: "High cost" },
];

export const Copilot = () => {
  useBreadcrumbs([{ label: "Admin", href: "/admin" }, { label: "Copilot" }]);

  const [shouldIncludeContext, setShouldIncludeContext] = useState(true);
  const [model, setModel] = useState(AVAILABLE_MODELS[0].id);
  const useAgent = !model.includes("-standalone");
  const [view, setView] = useState(
    localStorage.getItem("BeStory_copilotView") || "code",
  );

  const [systemPrompt, setSystemPrompt] = useState(
    localStorage.getItem("BeStory_copilotSystemPrompt") || "",
  );
  const [userPrompt, setUserPrompt] = useState("");

  const zoltar = useZoltarMutation();

  const zoltarData = useMemo(() => {
    try {
      if (!isZoltarGenerateOk(zoltar.data)) return {};
      return parseZoltarGenerateJson(zoltar.data, "{}");
    } catch (error) {
      console.warn("Failed to parse Zoltar response:", error, zoltar.data);
      return zoltar?.data?.envelope?.text ?? {};
    }
  }, [zoltar.data]);

  const getPromptTemplate = useCallback(() => {
    const template = [];

    if (shouldIncludeContext && zoltar.data?.messages?.length) {
      template.push(
        ...zoltarLangchainMessagesToPromptTemplate(zoltar.data.messages),
      );

      template.push({ role: "user", prompt_template: userPrompt });
    } else {
      if (systemPrompt) {
        template.push({ role: "system", prompt_template: systemPrompt });
      } else {
        // if (!useAgent) {
        // Had to add this to get responses from zoltar for simple questions
        template.splice(0, 0, {
          role: "system",
          prompt_template:
            "You are a helpful assistant that returns valid JSON responses.",
        });
        //         } else {
        //           template.splice(0, 0, {
        //             role: "system",
        //             prompt_template: `You are a helpful assistant.

        // Decision policy:
        // - Prefer answering from the last 10 messages. If those messages clearly contain the requested details, DO NOT call a tool.
        // - If the answer is not clearly present in the last 10 messages, apply the tool-selection rules below.

        // Tool-selection rules:
        // 1. **zoltar_live_search** (preferred for live / current data):
        //    - Use when the question asks about current events, recent news, trending topics, live data, real-time information, or anything that requires up-to-date web results.
        //    - Use when the question explicitly or implicitly refers to the latest year (2026) or recent timeframes ("this year", "latest", "current", "today", "recent", "new", "updated", "trending").
        //    - Call with: { "query": "<user question optimized for web search, include '2026' when relevant>" }
        //    - You may optionally pass a "system_prompt" to guide the search focus.

        // General rules:
        // - When calling any tool, set 'user' to exactly: {{email}} (where the parameter exists).
        // - When the user asks for "latest" information, always prefer zoltar_live_search and include the year 2026 in the query.

        // Output format:
        // - When you receive results from zoltar_live_search, ALWAYS format your final answer as a JSON object with this structure:
        // \`\`\`json
        // {
        //   "query": "<the search query used>",
        //   "results": [
        //     {
        //       "title": "<result title>",
        //       "summary": "<concise summary>",
        //       "source": "<source URL if available>"
        //     }
        //   ],
        //   "answer": "<synthesized answer from all results>"
        // }
        // \`\`\`
        // - If the search returns no results, return: { "query": "...", "results": [], "answer": "No results found." }

        // Return only valid JSON response.
        // `,
        //           });
        //         }
      }

      if (userPrompt) {
        template.push({ role: "user", prompt_template: userPrompt });
      }
    }

    return template;
  }, [systemPrompt, userPrompt, shouldIncludeContext, zoltar.data, useAgent]);

  const handleSend = useCallback(() => {
    const promptTemplate = getPromptTemplate();

    zoltar.mutate(
      {
        useAgent,
        promptTemplate,
        model,
        skills: "all",
      },
      {
        onSuccess: () => {
          setUserPrompt("");
        },
      },
    );
  }, [zoltar.mutate, useAgent, getPromptTemplate, model]);

  useEffect(() => {
    localStorage.setItem("BeStory_copilotSystemPrompt", systemPrompt);
  }, [systemPrompt]);

  useEffect(() => {
    localStorage.setItem("BeStory_copilotView", view);
  }, [view]);

  return (
    <>
      <title>{`Copilot | BeStory`}</title>
      <div
        className={style({
          display: "flex",
          flexDirection: "column",
          gap: 48,
          boxSizing: "border-box",
          width: "full",
          height: "calc(100vh - 6.5rem)",
          maxWidth: 1200,
          marginX: "auto",
        })}
      >
        {zoltar.isSuccess && (
          <SegmentedControl
            selectedKey={view}
            onSelectionChange={setView}
            styles={style({ marginStart: "auto" })}
          >
            <SegmentedControlItem id="preview" aria-label="Preview view">
              <TextAlignJustifyLastLeftIcon />
            </SegmentedControlItem>
            <SegmentedControlItem id="code" aria-label="Code view">
              <CodeIcon />
            </SegmentedControlItem>
          </SegmentedControl>
        )}
        <div
          className={style({
            // display: "flex",
            // flexDirection: "column",
            // alignItems: "center",
            // justifyContent: "center",
            flexGrow: 1,
            overflowY: "auto",
          })}
        >
          {zoltar.isPending && (
            <div
              className={style({
                size: "full",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              })}
            >
              <div
                className={style({
                  display: "flex",
                  alignItems: "end",
                  gap: 24,
                  marginX: "auto",
                })}
              >
                <ProgressBar isIndeterminate label="Generating response" />
                <ActionButton onPress={zoltar.reset}>Cancel</ActionButton>
              </div>
            </div>
          )}
          {zoltar.isSuccess && view === "preview" && (
            <div
              className={style({
                display: "flex",
                flexDirection: "column",
                width: "full",
                gap: 16,
              })}
            >
              {Object.entries(zoltarData).map(([key, value]) => (
                <div
                  id={key}
                  key={key}
                  className={style({
                    display: "flex",
                    flexDirection: "column",
                    gap: 8,
                    width: "full",
                    boxSizing: "border-box",
                    backgroundColor: "elevated",
                    boxShadow: "elevated",
                    borderRadius: "xl",
                    paddingX: 16,
                    paddingY: 12,
                  })}
                >
                  <div
                    className={style({
                      font: "detail",
                      color: "neutral-subdued",
                    })}
                  >
                    {key}
                  </div>
                  <div
                    className={style({
                      font: "body",
                      whiteSpace: "pre-wrap",
                    })}
                  >
                    {Array.isArray(value)
                      ? value.map((item, index) => (
                          <p key={index}>
                            {typeof item === "object"
                              ? JSON.stringify(item, null, 2)
                              : String(item)}
                          </p>
                        ))
                      : typeof value === "object"
                        ? JSON.stringify(value, null, 2)
                        : String(value)}
                  </div>
                </div>
              ))}
            </div>
          )}
          {zoltar.isSuccess && view === "code" && (
            <SyntaxHighlighter
              language="json"
              code={JSON.stringify(zoltarData, null, 2)}
            />
          )}
          {zoltar.isSuccess && !view && (
            <code
              className={style({
                font: "code",
                whiteSpace: "pre-wrap",
                alignSelf: "start",
              })}
            >
              {JSON.stringify(zoltarData, null, 2)}
            </code>
          )}
          {zoltar.isError && (
            <code
              className={style({
                font: "code",
                whiteSpace: "pre-wrap",
                alignSelf: "start",
              })}
            >
              {JSON.stringify(zoltar.error, null, 2)}
            </code>
          )}
        </div>

        <div className={style({ display: "flex", gap: 24, alignItems: "end" })}>
          <div
            className={style({
              display: "flex",
              flexDirection: "column",
              gap: 24,
              flexGrow: 1,
            })}
          >
            {!(
              zoltar.isPending ||
              (zoltar.isSuccess && shouldIncludeContext)
            ) && (
              <TextArea
                label="System prompt"
                value={systemPrompt}
                onChange={setSystemPrompt}
                styles={style({ flexGrow: 1, padding: 0 })}
                UNSAFE_className="__PromptInput"
              />
            )}
            <TextArea
              label={
                <>
                  <Text slot="description">User prompt</Text>
                  {zoltar.isSuccess && shouldIncludeContext && (
                    <Text
                      slot="description"
                      styles={style({ font: "detail-sm", marginStart: 8 })}
                    >
                      Previous conversation context will be included.
                    </Text>
                  )}
                </>
              }
              placeholder="Explain the concept of React hooks in simple terms."
              value={userPrompt}
              onChange={setUserPrompt}
              isDisabled={zoltar.isPending}
              styles={style({ flexGrow: 1, padding: 0 })}
              UNSAFE_className="__PromptInput"
            />
          </div>
          <div
            className={style({
              display: "flex",
              flexDirection: "column",
              gap: 16,
            })}
          >
            <Picker
              label="Model"
              selectedKey={model}
              onSelectionChange={setModel}
              isDisabled={shouldIncludeContext && zoltar.isSuccess}
            >
              {AVAILABLE_MODELS.map((m) => (
                <PickerItem key={m.id} id={m.id}>
                  <Text slot="label">{m.name}</Text>
                  <Text slot="description">{m.description}</Text>
                </PickerItem>
              ))}
            </Picker>
            <Switch
              isSelected={shouldIncludeContext}
              onChange={setShouldIncludeContext}
            >
              Include context
            </Switch>
            <ActionButton
              aria-label="Send"
              onPress={handleSend}
              isDisabled={zoltar.isPending}
            >
              <SendIcon />
            </ActionButton>
          </div>
        </div>
      </div>
    </>
  );
};
